import {Component, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import * as _ from 'lodash-es';
import {Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {createDisplaySymbol} from '../../common/display-symbol';
import {Exchange, EXCHANGE_NAMING} from '../../enums/exchange';
import {Security} from '../../models/security';
import {AppConfigService} from '../../services/config.service';
import {AutocompleteListComponent, DisplayVariant} from '../autocomplete/autocomplete-list.component';
import {
    SECURITY_KEY_TO_FIELD_NAME_DICT,
    SecurityAutocompleteFieldKey,
    SecurityAutocompleteItem,
    SecurityAutocompleteService,
    SecuritySearchVariant
} from './security-autocomplete.service';

export interface AutocompleteHighlightedSecurity {
    symbol: SafeHtml;
    exchange: SafeHtml;
}

export interface DisplaySecurityVariant extends DisplayVariant<Security> {
    variant: Security;
    highlightedSecurity: AutocompleteHighlightedSecurity;
}

@Component({
    selector: 'ul[securityAutocompleteList]',
    templateUrl: './security-autocomplete-list.component.html',
    styleUrls: ['./security-autocomplete-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SecurityAutocompleteListComponent extends AutocompleteListComponent<Security, DisplaySecurityVariant>
    implements OnInit {

    @Input()
    public term$: Subject<string>;

    private _autocompleteItems: SecurityAutocompleteItem[];

    private readonly appConfigService = this.injector.get(AppConfigService);
    private readonly domSanitizer = this.injector.get(DomSanitizer);
    private readonly securityAutocompleteService = this.injector.get(SecurityAutocompleteService);

    private term: string;

    public override ngOnInit(): void {
        super.ngOnInit();

        this.listenToTerm();
    }

    public override setVariants(variants: Security[], autocompleteItems?: SecurityAutocompleteItem[]): void {
        if (this._variants !== variants || this._autocompleteItems !== autocompleteItems) {
            this._variants = variants;
            this._autocompleteItems = autocompleteItems;
            this.updateVariants();
        }
    }

    protected override updateVariants(): void {
        this.displayVariants = this._autocompleteItems.map<DisplaySecurityVariant>(
            item => this.createDisplayVariant(item)
        );

        this.selectedVariant = _.head(this._variants);

        if (this.term) {
            this.updateDisplayVariantsByTerm(this.term);
        }

        this.updateIsEmpty();
    }

    protected createDisplayVariant(item: SecurityAutocompleteItem): DisplaySecurityVariant {
        return {
            variant: item.security,

            highlightedSecurity: !this.term
                         ? this.createHighlightedSecurity(item.security, item.searchTerms)
                         : null /** To be created by {@link updateDisplayVariantsByTerm} */
        };
    }

    private createHighlightedSecurity(
        security: Security,
        searchTerms: SecuritySearchVariant
    ): AutocompleteHighlightedSecurity {
        const symbol = this.getHighlightedSecurityPart(security, searchTerms, SecurityAutocompleteFieldKey.SYMBOL);
        const exchange = this.appConfigService.appConfig.displayExchange
            ? this.getHighlightedSecurityPart(security, searchTerms, SecurityAutocompleteFieldKey.EXCHANGE)
            : null;

        return {symbol, exchange};
    }

    private getTermRegExp(term: string): RegExp {
        const termEscapedWords = term
            .split(/\s+/)
            .map(_.escapeRegExp);

        /**
         * [TRAD-195] Use start-with in security search on UI
         */
        const regExpStr = `\\b(${termEscapedWords.join('|')})`;

        return new RegExp(regExpStr, 'gi');
    }

    private highlightString(str: string, term: string): string {
        let resultStr = str;

        if (str && term) {
            const regExp = this.getTermRegExp(term);

            resultStr = str.replace(regExp, match => `<mark>${match}</mark>`);
        }

        return resultStr;
    }

    private getSecurityPartByAutocompleteKey(security: Security, key: SecurityAutocompleteFieldKey): string {
        const securityFieldName = SECURITY_KEY_TO_FIELD_NAME_DICT[key];

        const securityFieldString = (key === SecurityAutocompleteFieldKey.EXCHANGE)
            ? EXCHANGE_NAMING[security[securityFieldName] as Exchange] || security[securityFieldName]
            : (key === SecurityAutocompleteFieldKey.SYMBOL)

                /**
                 * Calling of {@link createDisplaySymbol} should handle some special symbol formatting,
                 * e.g. for Autospreader securities, where some part of symbol should be hidden.
                 */
                ? createDisplaySymbol(security, true).symbol

                : security[securityFieldName];

        return String(securityFieldString);
    }

    private getHighlightedSecurityPart(
        security: Security,
        searchTerms: SecuritySearchVariant,
        key: SecurityAutocompleteFieldKey
    ): SafeHtml {
        const securityPart = this.getSecurityPartByAutocompleteKey(security, key);
        const markupStr = this.highlightString(securityPart, searchTerms[key]);

        return this.domSanitizer.bypassSecurityTrustHtml(markupStr);
    }

    private listenToTerm(): void {
        this.term$.pipe(
            distinctUntilChanged(),
            tap(term => this.term = term),
            tap(term => this.updateDisplayVariantsByTerm(term)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateDisplayVariantsByTerm(term: string): void {
        const searchVariants = this.securityAutocompleteService.getSearchVariants(term);
        let firstMatchedVariant: DisplaySecurityVariant;
        let isSelectedVariantMatched: boolean = true;

        this.displayVariants.forEach(displayVariant => {
            const searchTerms = searchVariants.find(currentSearchTerms =>
                this.isSecurityMatchedBySearchTerms(displayVariant.variant, currentSearchTerms)
            );

            displayVariant.highlightedSecurity = this.createHighlightedSecurity(
                displayVariant.variant,
                searchTerms || {}
            );

            if (searchTerms) {
                firstMatchedVariant = firstMatchedVariant || displayVariant;
            } else {
                isSelectedVariantMatched = this.selectedVariant === displayVariant.variant
                    ? false
                    : isSelectedVariantMatched;
            }
        });

        if (!isSelectedVariantMatched && firstMatchedVariant) {
            this.selectedVariant = firstMatchedVariant.variant;
        }
    }

    private isSecurityMatchedBySearchTerms(security: Security, searchTerms: SecuritySearchVariant): boolean {
        return (Object.keys(searchTerms) as SecurityAutocompleteFieldKey[])
            .every(key => {
                const securityPart = this.getSecurityPartByAutocompleteKey(
                    security,
                    key as unknown as SecurityAutocompleteFieldKey // Typecast string to the enum.
                );

                const regExp = this.getTermRegExp(searchTerms[key]);

                return regExp.test(securityPart);
            });
    }

}
